import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { X } from 'lucide-react';

type AnimationType =
  | 'scale'
  | 'slideRight'
  | 'slideLeft'
  | 'slideUp'
  | 'slideDown';
type PositionType = 'center' | 'right' | 'left' | 'top' | 'bottom';

const Modal = ({
  isOpen,
  setIsOpen,
  title,
  size = 'small',
  children,
  showCloseButton = true,
  footer,
  header = true,
  animation = 'scale',
  position = 'center',
  panelClassName = '',
  contentClassName = '',
}: {
  isOpen: boolean;
  setIsOpen: (e: boolean) => void;
  title: string;
  size?: 'small' | 'medium' | 'large';
  children: React.ReactNode;
  showCloseButton?: boolean;
  footer: React.ReactNode;
  header?: boolean;
  animation?: AnimationType;
  position?: PositionType;
  panelClassName?: string;
  contentClassName?: string;
}) => {
  const getWidthClasses = () => {
    switch (size) {
      case 'large':
        return 'w-full max-w-6xl';
      case 'medium':
        return 'w-full max-w-4xl';
      default:
        return 'w-full max-w-sm';
    }
  };

  const getTransitionProps = () => {
    switch (animation) {
      case 'slideRight':
        return {
          enter: 'ease-out duration-300',
          enterFrom: 'opacity-0 translate-x-full',
          enterTo: 'opacity-100 translate-x-0',
          leave: 'ease-in duration-200',
          leaveFrom: 'opacity-100 translate-x-0',
          leaveTo: 'opacity-0 translate-x-full',
        };
      case 'slideLeft':
        return {
          enter: 'ease-out duration-300',
          enterFrom: 'opacity-0 -translate-x-full',
          enterTo: 'opacity-100 translate-x-0',
          leave: 'ease-in duration-200',
          leaveFrom: 'opacity-100 translate-x-0',
          leaveTo: 'opacity-0 -translate-x-full',
        };
      case 'slideUp':
        return {
          enter: 'ease-out duration-300',
          enterFrom: 'opacity-0 translate-y-full',
          enterTo: 'opacity-100 translate-y-0',
          leave: 'ease-in duration-200',
          leaveFrom: 'opacity-100 translate-y-0',
          leaveTo: 'opacity-0 translate-y-full',
        };
      case 'slideDown':
        return {
          enter: 'ease-out duration-300',
          enterFrom: 'opacity-0 -translate-y-full',
          enterTo: 'opacity-100 translate-y-0',
          leave: 'ease-in duration-200',
          leaveFrom: 'opacity-100 translate-y-0',
          leaveTo: 'opacity-0 -translate-y-full',
        };
      case 'scale':
      default:
        return {
          enter: 'ease-out duration-300',
          enterFrom: 'opacity-0 scale-95',
          enterTo: 'opacity-100 scale-100',
          leave: 'ease-in duration-200',
          leaveFrom: 'opacity-100 scale-100',
          leaveTo: 'opacity-0 scale-95',
        };
    }
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'right':
        return 'items-center justify-end';
      case 'left':
        return 'items-center justify-start';
      case 'top':
        return 'items-start justify-center';
      case 'bottom':
        return 'items-end justify-center';
      case 'center':
      default:
        return 'items-center justify-center';
    }
  };

  const transitionProps = getTransitionProps();
  const positionClasses = getPositionClasses();

  return (
    <Transition show={isOpen}>
      <Dialog onClose={() => setIsOpen(false)} className="relative z-50">
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" />
        </TransitionChild>

        <TransitionChild
          enter={transitionProps.enter}
          enterFrom={transitionProps.enterFrom}
          enterTo={transitionProps.enterTo}
          leave={transitionProps.leave}
          leaveFrom={transitionProps.leaveFrom}
          leaveTo={transitionProps.leaveTo}
        >
          <div className={`fixed inset-0 flex w-screen p-4 ${positionClasses}`}>
            <DialogPanel
              className={`${getWidthClasses()} max-h-[calc(100dvh-150px)] flex flex-col overflow-hidden bg-white divide-y rounded-xl ${panelClassName}`}
            >
              {header && (
                <div className="shrink-0 px-4 py-4 sm:px-6 h-12 text-xl font-medium leading-tight flex items-center border-none shadow-md">
                  <DialogTitle className="shrink grow text-black">
                    {title}
                  </DialogTitle>
                  {showCloseButton ? (
                    <button
                      className="shrink-0"
                      onClick={() => setIsOpen(false)}
                    >
                      <X className="size-6" />
                    </button>
                  ) : null}
                </div>
              )}
              <div
                className={`shrink grow overflow-hidden overflow-y-auto ${contentClassName}`}
              >
                {children}
              </div>

              {footer && footer}
            </DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default Modal;
